import {
  isIntegrationProvider,
  type IntegrationProvider,
} from "@repo/app-types/entities/integration";
import { useEffect, useState, type FC } from "react";

export const OAUTH_INTEGRATION_CALLBACK_QUERY_PARAM_NAME =
  "oauthIntegrationCallback";

const INTEGRATION_CONNECTED_CHANNEL_NAME = "alpharun:integration-connected";

export interface IntegrationConnectedMessage {
  provider: IntegrationProvider;
}

/**
 * True when this window is the OAuth popup landing back on the app after the
 * provider's callback. The API redirects here rather than rendering its own
 * page so that the popup ends up on the app's origin, which is what lets it
 * announce the new integration on the BroadcastChannel below.
 */
export function getIsOAuthIntegrationCallback(): boolean {
  const searchParams = new URLSearchParams(window.location.search);
  return (
    searchParams.get(OAUTH_INTEGRATION_CALLBACK_QUERY_PARAM_NAME) === "true"
  );
}

/**
 * Announces a newly connected integration to the app. Deliberately not
 * postMessage-to-opener: the API sets `Cross-Origin-Opener-Policy: same-origin`
 * (helmet's default), which severs `window.opener` across the OAuth redirect, so
 * the popup has no handle on the window that opened it. A BroadcastChannel needs
 * no such handle — only a shared origin.
 */
export function subscribeToIntegrationConnected(
  onConnected: (message: IntegrationConnectedMessage) => void,
): () => void {
  const channel = new BroadcastChannel(INTEGRATION_CONNECTED_CHANNEL_NAME);
  channel.onmessage = (event: MessageEvent<IntegrationConnectedMessage>) => {
    onConnected(event.data);
  };
  return () => {
    channel.close();
  };
}

export const OAuthPopupCallbackPage: FC = () => {
  const [hasFailed, setHasFailed] = useState(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const provider = searchParams.get("provider");
    const isSuccess = searchParams.get("status") === "success";

    if (!isSuccess || provider === null || !isIntegrationProvider(provider)) {
      setHasFailed(true);
      return;
    }

    const channel = new BroadcastChannel(INTEGRATION_CONNECTED_CHANNEL_NAME);
    channel.postMessage({ provider } satisfies IntegrationConnectedMessage);
    channel.close();

    window.close();
  }, []);

  return (
    <div className="p-4 text-center text-sm">
      {hasFailed
        ? "An error occurred while adding this integration. Please close this window and try again."
        : "Integration connected - you can close this window and return to Alpharun."}
    </div>
  );
};
